import Item from "@/components/Item";
import { useItemModal } from "@/contexts/ItemModalProvider";
import { Loader2, Store, Utensils } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import heartAnimation from "@/assets/favourite.json";
import Lottie from "lottie-react";
import { useFavourites } from "@/contexts/FavouritesProvider";
import Company from "@/components/Company";

export default function FavouritesScreen() {
  const [activeTab, setActiveTab] = useState<"restaurants" | "items">(
    "restaurants"
  );

  const navigate = useNavigate();
  const { open } = useItemModal();
  const { favouriteItems, favouriteCompanies, removeFavouriteItem } =
    useFavourites();

  // detect RTL from document dir
  const isRTL =
    typeof document !== "undefined" && document.documentElement.dir === "rtl";

  const isEmpty =
    activeTab === "restaurants"
      ? favouriteCompanies.length === 0
      : favouriteItems.length === 0;

  return (
    <div className="px-4 sm:px-8 md:px-12 py-8 flex flex-col gap-8">
      {/* Tabs */}
      <div className="relative mx-auto w-full max-w-md flex gap-2 justify-center bg-gray-200 p-1 rounded-full">
        {/* Sliding background */}

        <div
          className={`flex flex-1 items-center justify-center gap-1 sm:gap-2 py-2 px-2 sm:px-4 text-sm sm:text-base font-medium transition-all duration-300 rounded-full cursor-pointer z-10 ${
            activeTab === "restaurants" ? "bg-white" : ""
          }`}
          onClick={() => setActiveTab("restaurants")}
        >
          <Store className="size-4 sm:size-5" />
          <p>Restaurants</p>
        </div>
        <div
          className={`flex flex-1 items-center justify-center gap-1 sm:gap-2 py-2 px-2 sm:px-4 text-sm sm:text-base font-medium transition-all duration-300 rounded-full cursor-pointer z-10 ${
            activeTab === "items" ? "bg-white" : ""
          }`}
          onClick={() => setActiveTab("items")}
        >
          <Utensils className="size-4 sm:size-5" />
          <p>Items</p>
        </div>
      </div>

      {!isEmpty && (
        <div className="flex items-center gap-4">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-wide">
            Your Favourites
          </h1>
        </div>
      )}

      {/* Empty State */}
      <div
        className={` flex-col items-center justify-center text-center ${
          !isEmpty ? "hidden" : "flex"
        }`}
      >
        <div className="w-40 h-40 sm:w-48 sm:h-48">
          <Lottie
            animationData={heartAnimation}
            loop={false}
            className="w-full h-full"
          />
        </div>
        <div className="flex flex-col items-center justify-center mt-[-20px] gap-2">
          <h1 className="text-xl sm:text-2xl font-bold tracking-wide">
            No {activeTab === "restaurants" ? "restaurants" : "items"} yet
          </h1>
          <p className="text-sm text-gray-500">
            Start exploring and add your favourite{" "}
            {activeTab === "restaurants" ? "restaurants" : "items"} to see them
            here
          </p>
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
        {activeTab === "restaurants" &&
          favouriteCompanies.map((company) => (
            <Company key={company.id} company={company} />
          ))}
        {activeTab === "items" &&
          favouriteItems.map((item) => (
            <Item
              key={item.id}
              showFavouriteButton={true}
              isFavourite={true}
              onFavouriteClick={() => {
                removeFavouriteItem(item);
              }}
              name={item.name}
              imageUrl={item.image || undefined}
              price={item.price}
              restaurantName={item.companyName}
              onClick={() => {
                open({
                  item,
                  ctaText: "Order",
                  onCtaClick: () => {
                    navigate(`/restaurants/${item.companyId}/items/${item.id}`);
                  },
                });
              }}
            />
          ))}
      </div>
    </div>
  );
}
